// src/features/reports/ReportTile.jsx
//
// REPORTS-TILES-V2-HERO-NUMBER — gradient banner with hero metric.
//
// Replaces the flat hero-illustration band with a colored gradient banner.
// The banner contains the icon glyph chip, title, subtitle (kicker), and
// the favorite star pinned top-right. Below them, the hero metric occupies
// the lower section of the banner:
//   • liveCount prop → large number + unit label (Headcount Trend, Open Roles)
//   • ragCounts prop → three RAG pills (WPR)
//
// The white card body holds the description, tag row, and "View report →" footer.
//
// The card is made interactive via role="button" on the outer card div so that
// the entire card (banner + body) is clickable without nesting <button> inside
// <button> (an HTML spec violation). The favorite star is a proper <button>
// with e.stopPropagation() so its click never triggers onSelectReport.
//
// Props:
//   tile             — { key, title, subtitle, svgPath, description, tags }
//   onSelectReport(key) — called on tile click
//   isFavorite       — whether this tile is starred
//   onToggleFavorite — called to toggle the favorite star
//   liveCount        — "loading" | number | null
//                        "loading" → spinner while fetching
//                        number   → resolved value (animates in via useCountUp)
//                        null     → honest "—" (fetch failed or no usable value)
//                        undefined → section omitted entirely
//   liveLabel        — string         (unit label, default "open roles")
//   liveSuffix       — string         (unit joined directly to the value, e.g. "%")
//   ragCounts        — "loading" | { onTrack, atRisk, critical } | null
//                        follows the same 3-way sentinel as liveCount
//                        undefined → section omitted entirely

import React from "react";
import { Star } from "lucide-react";
import { T } from "../../app/tokens.js";
import { REPORT_ACCENTS } from "../../app/tokens.js";
import { useCountUp } from "../../v2/hooks/useCountUp.js";
import { KpiSpinner } from "./KpiSpinner.jsx";

// Fallback gradient for unknown tile keys. The actual value lives in tokens.js
// (a plain .js file) so no raw hex appears in this .jsx file (L52 sweep rule).
const BANNER_GRAD_FALLBACK = T.reportBannerGradFallback;

function RagPill({ count, label, dotColor, testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "rgba(255,255,255,0.18)",
        borderRadius: 6,
        padding: "2px 10px",
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        color: T.white,
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: dotColor,
          flexShrink: 0,
          display: "inline-block",
        }}
      />
      {count} {label}
    </span>
  );
}

export function ReportTile({
  tile,
  onSelectReport,
  isFavorite,
  onToggleFavorite,
  liveCount,
  liveLabel = "open roles",
  liveSuffix = "",
  liveFormat = "number",
  livePrecision,
  ragCounts,
}) {
  // Count-up for the resolved numeric value. useCountUp must be called
  // unconditionally (hooks rules). When liveCount is not a number we pass 0
  // as both target and key — the animation won't fire because the key stays
  // stable. When liveCount transitions from "loading" → number the key flips
  // and the ease-out animation plays. In JSDOM / reduced-motion environments
  // useCountUp returns the target immediately with no rAF.
  const countTarget  = typeof liveCount === "number" ? liveCount : 0;
  const countAnimKey = typeof liveCount === "number" ? liveCount : null;
  const displayCount = useCountUp(countTarget, countAnimKey, { from: 0, duration: 400 });
  const formattedCount = liveFormat === "compactCurrency"
    ? (Math.abs(displayCount) >= 1e6
      ? `$${(displayCount / 1e6).toFixed(1)}M`
      : Math.abs(displayCount) >= 1e3
        ? `$${(displayCount / 1e3).toFixed(1)}K`
        : `$${Math.round(displayCount)}`)
    : displayCount.toLocaleString(undefined, livePrecision == null ? undefined : {
      minimumFractionDigits: livePrecision,
      maximumFractionDigits: livePrecision,
    });

  const { acc, tint, grad } = REPORT_ACCENTS[tile.key] || {
    acc: T.mid,
    tint: T.lightGray,
    grad: BANNER_GRAD_FALLBACK,
  };

  function handleCardClick() {
    if (onSelectReport) onSelectReport(tile.key);
  }

  function handleCardKeyDown(e) {
    if (e.key === "Enter" || e.key === " ") {
      // If the key event originated inside a child <button> (e.g. the favorite
      // star), that button handles its own action — don't also activate the card.
      if (e.target.closest("button")) return;
      e.preventDefault();
      if (onSelectReport) onSelectReport(tile.key);
    }
  }

  return (
    <div
      data-testid="report-tile-wrapper"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/*
        The entire card is the interactive element (role="button") so that both
        the banner and the body trigger onSelectReport on click. This avoids
        nesting <button> inside <button>. The favorite star is a proper <button>
        with e.stopPropagation() to intercept its own click.
      */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open ${tile.title} report`}
        className="wos-card-interactive"
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          flex: 1,
          background: T.card,
          borderRadius: 12,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {/* ── Gradient banner ─────────────────────────────────────────── */}
        <div
          style={{
            background: grad,
            padding: "14px 16px 18px",
            flexShrink: 0,
          }}
        >
          {/* Top row: icon chip + title/subtitle + star */}
           <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minHeight: 44 }}>
            {/* Icon chip */}
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={T.white}
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d={tile.svgPath} />
              </svg>
            </div>

            {/* Title + subtitle */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: T.fsBase5,
                  fontWeight: T.fwBold,
                  color: T.white,
                  letterSpacing: -0.2,
                  lineHeight: 1.3,
                }}
              >
                {tile.title}
              </div>
              <div
                style={{
                  fontSize: T.fsXs,
                  color: "rgba(255,255,255,0.75)",
                  fontWeight: T.fwSemibold,
                  marginTop: 2,
                }}
              >
                {tile.subtitle}
              </div>
            </div>

            {/* Favorite star — a real <button> that stops propagation */}
            {onToggleFavorite && (
              <button
                type="button"
                data-testid={`report-favorite-${tile.key}`}
                aria-label={
                  isFavorite
                    ? `Remove ${tile.title} from favorites`
                    : `Add ${tile.title} to favorites`
                }
                aria-pressed={isFavorite}
                onKeyDown={(e) => {
                  // Prevent Enter/Space from bubbling to the parent card
                  // div[role="button"] and triggering onSelectReport.
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(255,255,255,0.18)",
                  backdropFilter: "blur(2px)",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <Star
                  size={15}
                  color={T.white}
                  fill={isFavorite ? T.white : "none"}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>

          {/* Hero number — shown when liveCount is defined.
               liveCount === undefined  → tile has no live-count section (omit entirely)
               liveCount === "loading" → spinner while fetching
               liveCount === null      → honest "—" (fetch failed / no usable value)
               liveCount === <number>  → animated count-up via useCountUp */}
          {liveCount !== undefined && (
            <div
              style={{
                marginTop: 14,
                display: "flex",
                alignItems: "center",
                gap: 6,
                minHeight: 34,
              }}
            >
              {liveCount === "loading" ? (
                <KpiSpinner />
              ) : liveCount === null ? (
                <span
                  data-testid="hero-live-count-empty"
                  style={{
                    fontSize: T.fs2xl,
                    fontWeight: T.fwDisplay,
                    color: "rgba(255,255,255,0.5)",
                    lineHeight: 1,
                  }}
                >
                  —
                </span>
              ) : (
                <>
                  <span
                    data-testid="hero-live-count"
                    style={{
                      fontSize: T.fs2xl,
                      fontWeight: T.fwDisplay,
                      color: T.white,
                      lineHeight: 1,
                    }}
                  >
                     {formattedCount}{liveSuffix}
                  </span>
                  <span
                    data-testid="hero-live-label"
                    style={{
                      fontSize: T.fsSm,
                      color: "rgba(255,255,255,0.75)",
                      fontWeight: T.fwSemibold,
                    }}
                  >
                    {liveLabel}
                  </span>
                </>
              )}
            </div>
          )}

          {/* RAG pills — shown when ragCounts is defined (WPR tile).
               ragCounts === undefined  → tile has no RAG-pill section (omit entirely)
               ragCounts === "loading" → spinner while fetching
               ragCounts === null      → honest "—" (fetch failed / no usable value)
               ragCounts === <object>  → render the real pills */}
          {ragCounts !== undefined && (
            <div
              style={{
                marginTop: 14,
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
                minHeight: 26,
              }}
            >
              {ragCounts === "loading" ? (
                <KpiSpinner />
              ) : ragCounts === null ? (
                <span
                  data-testid="rag-pills-empty"
                  style={{
                    fontSize: T.fs2xl,
                    fontWeight: T.fwDisplay,
                    color: "rgba(255,255,255,0.5)",
                    lineHeight: 1,
                  }}
                >
                  —
                </span>
              ) : (
                <>
                  <RagPill
                    count={ragCounts.onTrack}
                    label="on track"
                    dotColor={T.green}
                    testId="rag-pill-on-track"
                  />
                  <RagPill
                    count={ragCounts.atRisk}
                    label="at risk"
                    dotColor={T.amber}
                    testId="rag-pill-at-risk"
                  />
                  <RagPill
                    count={ragCounts.critical}
                    label="critical"
                    dotColor={T.red}
                    testId="rag-pill-critical"
                  />
                </>
              )}
            </div>
          )}
        </div>

        {/* ── White card body ──────────────────────────────────────────── */}
        <div
          style={{
            padding: 20,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            minHeight: 0,
          }}
        >
          {/* Description — clamped to 2 lines */}
          <p
            style={{
              margin: 0,
              fontSize: T.fsSm,
              color: T.mid,
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {tile.description}
          </p>

          {/* Tags */}
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginTop: "auto",
              paddingTop: 4,
            }}
          >
            {tile.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: T.fsXxs,
                  fontWeight: T.fwSemibold,
                  color: acc,
                  background: tint,
                  padding: "2px 10px",
                  borderRadius: 6,
                  letterSpacing: 0.3,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Footer: "View report →" pinned to the bottom */}
        <div
          style={{
            borderTop: `1px solid ${T.border}`,
            padding: "10px 20px",
            background: T.lightGray,
            display: "flex",
            justifyContent: "flex-end",
            fontSize: T.fsSm,
            fontWeight: T.fwBold,
            color: acc,
            letterSpacing: 0.2,
          }}
        >
          View report →
        </div>
      </div>
    </div>
  );
}
